<template>
  <employee-profile>
    <modal-dialog-prevent-leave
      v-bind:is-shown="isShownDialogLeaveConfirm"
      v-on:back="isShownDialogLeaveConfirm = false"
      v-on:leave="leaveWithoutSave"
      v-on:save="saveAndLeave" />
    <form
      class="employee-egisz"
      v-on:submit.prevent="onSubmitEmployeeEdit"
    >
      <multiline-select
        attach
        class="employee-egisz__multiline-select"
        item-text="title"
        item-value="value"
        label="Должность"
        v-bind:violations="nsiPositionInputErrors"
        v-bind:items="nsiPositionList"
        v-model="employeeEgisz.nsiPositionsIds"
        v-on:input="checkChanged"
      />
      <multiline-select
        attach
        class="employee-egisz__multiline-select employee-egisz__specialty"
        item-text="title"
        item-value="value"
        label="Специальность"
        v-bind:violations="specialtyInputErrors"
        v-bind:items="employeeSpecialtyOptionList"
        v-model="employeeEgisz.specialtiesIds"
        v-on:input="checkChanged"
      />
      <the-select
        class="employee-egisz__select"
        title="Выберите уровень конфиденциальности"
        v-bind:optional="true"
        v-bind:options="confidentialityLevelList"
        v-bind:violations="confidentialityLevelErrors"
        v-model="employeeEgisz.confidentialityLevelId"
        v-on:input="checkChanged"
      />
      <button
        class="employee-egisz__button-save"
        type="submit"
        v-bind:disabled="isAwaitRequest"
      >
        Сохранить
      </button>
    </form>
  </employee-profile>
</template>

<script>
import TheSelect from '@/components/TheSelect.vue';
import EmployeeProfile from '@/views/ClinicPanel/EmployeeProfile.vue';
import { EmployeeEgiszEditRequest } from '@/storage/gateways/EmployeeGateway';
import notifierMixin from '@/mixins/notifierMixin';
import preventLeaveWithoutConfirmMixin from '@/mixins/preventLeaveWithoutConfirmMixin';
import ModalDialogPreventLeave from '@/components/ModalDialogPreventLeave.vue';
import debounce from 'lodash.debounce';
import MultilineSelect from '@/components/MultilineSelect.vue';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.profileInputErrors ? vm.profileInputErrors[cause] : []
);

function EmployeeEgisz(
  confidentialityLevelId = '',
  nsiPositionsIds = [],
  specialties = [],
) {
  this.confidentialityLevelId = confidentialityLevelId;
  this.nsiPositionsIds = nsiPositionsIds;
  this.specialtiesIds = specialties;

  this.toString = () => JSON.stringify({
    confidentialityLevelId: this.confidentialityLevelId,
    nsiPositions: this.nsiPositionsIds,
    specialtiesIds: this.specialtiesIds,
  });

  let initial = this.toString();
  this.isChanged = () => this.toString() !== initial;

  /**
   * @description Обновить.
   * @returns {void}
   */
  this.refresh = () => {
    initial = toString();
  };
}

/**
 * @description Компонент пункта профиля сотрудника "Основное".
 * @type {Vue}
 */
export default {
  computed: {
    needPreventLeave() {
      return this.isChanged;
    },
    employeeId: (vm) => parseInt(vm.$route.params.id, 10),
    defaultConfidentialityLevelId() {
      return this.confidentialityLevelList.slice(0, 1).map(({ value }) => value).at(0);
    },
    allowedEgisz() {
      return this.isAdmin !== true || this.isEdit;
    },
    /**
     * @description Является ли сотрудник администратором.
     * @return {boolean}
     */
    isAdmin() {
      return this.roleId === 'admin';
    },
    /**
     * @description Форма открыта для редактирования.
     * @returns {boolean}
     */
    isEdit() {
      return this.initialId !== null;
    },
    confidentialityLevelErrors: resolveInputErrors('confidentiality-level'),
    nsiPositionInputErrors: resolveInputErrors('nsi-position'),
    snilsInputErrors: resolveInputErrors('snils'),
    specialtyInputErrors: resolveInputErrors('specialty-id'),
  },
  components: {
    MultilineSelect,
    ModalDialogPreventLeave,
    EmployeeProfile,
    TheSelect,
  },
  created() {
    this.fetchProfile();
    this.fetchProfileOptions();
  },
  data() {
    return {
      employeeEgisz: new EmployeeEgisz(),
      confidentialityLevelList: [],
      employeeSpecialtyOptionList: [],
      nsiPositionList: [],
      isAwaitRequest: false,
      profileInputErrors: {},
      isChanged: false,
    };
  },
  methods: {
    fetchProfile() {
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const onSuccess = async (employee) => {
        this.profileInputErrors = {};
        const specialitiesIds = employee.specialities.map((speciality) => speciality.id);
        const nsiPositionsIds = employee.nsiPositions.map((position) => position.id);
        this.employeeEgisz = new EmployeeEgisz(
          employee.confidentialityLevelId || this.defaultConfidentialityLevelId,
          nsiPositionsIds,
          specialitiesIds,
        );
      };
      const response = this.$employeeGateway.getById(this.employeeId);
      response.then((result) => result.fold(onFail, onSuccess));
    },
    fetchProfileOptions() {
      const onResponse = (result) => {
        this.confidentialityLevelList = result
          .confidentialityLevels
          .map(({ name: title, id: value }) => ({ title, value }));
        this.employeeSpecialtyOptionList = result
          .employeeSpecialtyList
          .map(({ name: title, id: value }) => ({ title, value }));
        this.nsiPositionList = result
          .nsiPositionList
          .map(({ name: title, id: value }) => ({ title, value }));
      };
      this.$suggestionGateway.getEmployeeCreation().then(onResponse);
    },
    /**
     * @description Отправить событие отправки редактирования сотрудника.
     * @returns {Promise}
     */
    onSubmitEmployeeEdit() {
      const request = this.makeEmployeeEgiszEditingRequest();
      this.isAwaitRequest = true;
      const onFail = (error) => {
        this.profileInputErrors = error.allCauses();
        if (!error.hasCauses()) {
          this.notifyFail(error.getTitle());
        }
        this.isAwaitRequest = false;
      };
      const onSuccess = (result) => {
        this.notifySuccess(result.title);
        this.profileInputErrors = {};
        this.isAwaitRequest = false;
        this.isChanged = false;
        this.employeeEgisz.refresh();
      };
      const response = this.$employeeGateway.editEgisz(request);
      response.then((result) => result.fold(onFail, onSuccess));
      return response;
    },
    /**
     * @description Создать запрос на редактирование данных ЕГИСЗ
     * @returns
     */
    makeEmployeeEgiszEditingRequest() {
      return new EmployeeEgiszEditRequest(
        this.employeeId,
        this.employeeEgisz.confidentialityLevelId,
        this.employeeEgisz.nsiPositionsIds,
        this.employeeEgisz.specialtiesIds,
      );
    },
    async saveAndLeave() {
      const result = await this.onSubmitEmployeeEdit();
      result.forEach(({ message }) => {
        this.notifySuccess(message);
        this.actionAfterClosingDialogLeaveConfirm();
      });
      this.isShownDialogLeaveConfirm = false;
    },
    /**
     * @description Проверить наличие изменений.
     * @returns {void}
     */
    checkChanged: debounce(function _() {
      this.isChanged = this.employeeEgisz.isChanged();
    }, 300),
  },
  mixins: [
    notifierMixin,
    preventLeaveWithoutConfirmMixin,
  ],
  name: 'ClinicPanelEmployeeProfileEgisz',
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.employee-egisz {
  display: grid;
  grid-template-columns: 2.1fr 0.98fr;
  gap: 20px;
  margin: 37px 0 0 0;
  width: 100%;
  max-width: 700px;
  &__multiline-select {
    grid-column: span 2;
  }
  &__select {
    @media screen and (max-width: 520px) {
      grid-column: span 2;
    }
  }
  &__button-save {
    @include buttons.btn-blue(100%, 48px, 16px);
    grid-column: 1/2;
    max-width: 174px;
    margin: 40px 0 0 0 !important;
  }
}
</style>
